"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"

const FALLBACK_RELATED = [
  "kegerator-keg-yield-calculator",
  "kegerator-size-calculator",
  "energy-cost-calculator",
  "refrigerator-size-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Kegerator Beer Line Calculator",
  category: "Size & Capacity" as const,
  mediumDescription:
    "Balance your draft system. Calculates the recommended beer-line length to drop keg pressure to a clean 1–2 PSI at the tap, factoring line diameter, run length, and vertical rise.",
  faqs: [
    {
      question: "What pressure should the keg be set to?",
      answer:
        "Most US ales and lagers serve cleanly at 10–14 PSI of CO₂ at 36–38°F. The line resistance has to bring that pressure down to roughly 1 PSI at the tap, otherwise you get foam.",
    },
    {
      question: "Why does line diameter matter so much?",
      answer:
        "Smaller-diameter line creates more friction. 3/16\" line drops about 3 PSI per foot, 1/4\" drops about 2 PSI/ft, and 5/16\" only about 0.5 PSI/ft — so thicker line needs to be much longer to balance.",
    },
    {
      question: "How does vertical rise change the math?",
      answer:
        "Each foot of vertical rise from keg to tap adds about 0.5 PSI of resistance because gravity is doing some of the work. That's added on top of the line-friction resistance.",
    },
    {
      question: "What does a balanced pour look like?",
      answer:
        "About 4–6 seconds for a 12 oz pour, with roughly one inch of head. Pours faster than 4 seconds usually mean the line is too short for your pressure; slower than 6 seconds means too long.",
    },
    {
      question: "How often should beer lines be cleaned?",
      answer:
        "Industry guidance from the Brewers Association recommends cleaning lines every two weeks with a dedicated line cleaner. Skipping this lets beer-stone and yeast buildup degrade flavor and cause foaming.",
    },
  ],
  relatedTools: ["kegerator-keg-yield-calculator", "kegerator-size-calculator", "energy-cost-calculator"],
}

export function BeerLineCalculatorPageClient() {
  const [distance, setDistance] = useState("5")
  const [rise, setRise] = useState("0")
  const [lineDiameter, setLineDiameter] = useState("0.25")
  const [temperature, setTemperature] = useState("38")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const dist = parseFloat(distance)
    const riseHeight = parseFloat(rise)
    const diameter = parseFloat(lineDiameter)
    const temp = parseFloat(temperature)
    if (!dist || !diameter || isNaN(temp) || dist <= 0) return null

    let resistancePerFoot = 0
    if (diameter === 0.1875) resistancePerFoot = 3
    else if (diameter === 0.25) resistancePerFoot = 2
    else resistancePerFoot = 0.5

    const safeRise = isNaN(riseHeight) ? 0 : riseHeight
    const riseResistance = safeRise * 0.5
    const resistance = dist * resistancePerFoot + riseResistance

    const targetPSI = 12
    const recommendedLength = Math.round((targetPSI - riseResistance) / resistancePerFoot)
    const pourTime = 5 + (dist - recommendedLength) * 0.5

    let status: "perfect" | "too-short" | "too-long" = "perfect"
    let statusLabel = "Well-balanced — should pour cleanly"
    if (dist < recommendedLength - 2) {
      status = "too-short"
      statusLabel = `Too short — add ${recommendedLength - dist} ft of line`
    } else if (dist > recommendedLength + 2) {
      status = "too-long"
      statusLabel = `Too long — remove ${dist - recommendedLength} ft of line`
    }

    let tempWarning: string | null = null
    if (temp > 40) tempWarning = "Beer is too warm — keep at 36–38°F"
    else if (temp < 34) tempWarning = "Beer is too cold — CO₂ may absorb instead of carbonating"

    return {
      recommendedLength,
      resistance: parseFloat(resistance.toFixed(1)),
      targetPSI,
      pourTime: parseFloat(pourTime.toFixed(1)),
      status,
      statusLabel,
      tempWarning,
    }
  }, [distance, rise, lineDiameter, temperature])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="The right line length for"
      heroTitleItalic="zero"
      heroTitlePost="foam."
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~20 sec", "Brewers Association draft balance"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "10–14", label: "Keg PSI", note: "Standard CO₂" },
            { value: "36–38°F", label: "Beer temperature", note: "Cold side" },
            { value: "2 PSI/ft", label: "1/4\" line drop", note: "Resistance" },
            { value: "4–6 sec", label: "12 oz pour", note: "Clean target" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "kegerator-beer-line-calculator")}
      cta={{
        title: "Cold beer needs the right cold box.",
        body: "Browse beverage centers and undercounter refrigerators sized for kegerator conversions and home bars.",
        buttonLabel: "Browse beverage centers",
        buttonHref: "/collections/beverage-coolers",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Run length.
                <br />
                Line diameter.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                We balance your line resistance against a 12 PSI keg target.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Run length
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={distance}
                      onChange={(e) => setDistance(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">ft</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Vertical rise
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={rise}
                      onChange={(e) => setRise(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">ft</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Line diameter
                  </label>
                  <select
                    value={lineDiameter}
                    onChange={(e) => setLineDiameter(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="0.1875">3/16&quot; — high resistance, long runs</option>
                    <option value="0.25">1/4&quot; — standard kegerator</option>
                    <option value="0.3125">5/16&quot; — low resistance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Beer temperature
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={temperature}
                      onChange={(e) => setTemperature(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">°F</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Above 40°F creates foam regardless of line balance.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Recommended line length
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}
                  >
                    {result?.recommendedLength ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">ft</span>
                  </div>
                  <div className="mt-4 text-[15px] text-white/85 leading-[1.5]">
                    {result?.statusLabel ?? "Enter run length and diameter."}
                  </div>
                  {result?.tempWarning && (
                    <div className="mt-2 text-[13px] text-[#ffb780] font-semibold">{result.tempWarning}</div>
                  )}
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Resistance</div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">{result?.resistance ?? "—"} PSI</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Target keg</div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">{result?.targetPSI ?? "—"} PSI</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Pour time</div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">{result?.pourTime ?? "—"}s</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Cold infrastructure"
          title="Counter-depth fridges for kegerator conversions"
          description="Undercounter cold boxes with adjustable shelving — the right base for tap setups."
          collectionType="counter-depth"
          fallbackCollectionType="counter-depth"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Resistance math, <em className="italic text-[#ff9951]">honest.</em>
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Line-resistance values come from Brewers Association draft beer quality guidance for vinyl beer line at 38°F.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Line resistance",
                  d: "3/16\" = ~3 PSI/ft, 1/4\" = ~2 PSI/ft, 5/16\" = ~0.5 PSI/ft. Multiply by run length.",
                },
                {
                  n: "02",
                  t: "Vertical rise",
                  d: "Add 0.5 PSI for each foot of vertical rise from keg to tap.",
                },
                {
                  n: "03",
                  t: "Balanced length",
                  d: "Recommended length = (12 PSI − rise resistance) ÷ resistance per foot. Within ±2 ft = balanced.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth fridges that work as a home bar"
          description="Built-in or freestanding 24-inch units — ideal companion cold storage to a tap setup."
          collectionType="counter-depth"
          fallbackCollectionType="counter-depth"
        />
      </section>
    </ToolPageShell>
  )
}
